import {
  PAY_BASIS_TO_EXPRESSED_AS,
  formatPayRangeLocationLabel,
} from "@components/PayRangeModal/payRangeDraftPayload";
import {
  PAY_RANGE_STATUS,
  archivedTag,
  normalizeVersionStatus,
  rangeStatusBadge,
} from "@components/FairPay/payRangeBadges";
import { resolveCorrectedByName } from "@components/PayRangeCorrectedBadge/payRangeCorrectedBadgeUtils";
import { formatFairPayDate } from "@components/utils/fairPayFormat";
import { formatPayRangeAmount } from "@components/utils/formatPayRangeAmount";

export const WORK_MODE_LABEL = {
  ON_SITE: "On-Site",
  HYBRID: "Hybrid",
  REMOTE: "Remote",
  UNSPECIFIED: "Unspecified",
};

export const PAY_BASIS_UNIT = {
  HOURLY: "hour",
  MONTHLY: "month",
  ANNUAL: "year",
};

/**
 * Amount prefixes shown on the detail page. USD keeps the native "$" symbol for
 * parity with the rest of the app; every other currency falls back to its code
 * (e.g. "RON 3K") so non-USD pay ranges no longer render a hardcoded "$"
 * (ER-11388).
 */
const CURRENCY_SYMBOLS = { USD: "$" };

export function toNumber(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function resolveFlag(country = {}) {
  const explicit =
    country.flag || country.icon || country.flag_icon || country.flag_url || "";
  if (explicit) return explicit;
  const code = String(country.code || "")
    .trim()
    .toLowerCase();
  if (!code || code.length !== 2) return "";
  return `https://flagcdn.com/${code}.svg`;
}

function formatCompact(n, currencyCode = "USD") {
  if (n == null || Number.isNaN(Number(n))) return "—";
  const prefix = CURRENCY_SYMBOLS[currencyCode] || `${currencyCode} `;
  return `${prefix}${formatPayRangeAmount(n)}`;
}

/**
 * Readiness ladder: 0% / 50% / 100% / 150% → min … max (same as list mapper).
 */
export function buildReadiness(min, max, currencyCode = "USD") {
  const lo = toNumber(min);
  const hi = toNumber(max);
  if (lo == null || hi == null) return [];
  const span = Math.max(hi - lo, 0);
  return [
    { label: "0%", value: formatCompact(lo, currencyCode), tone: "neutral" },
    { label: "50%", value: formatCompact(lo + span / 3, currencyCode), tone: "danger" },
    { label: "100%", value: formatCompact(lo + (span * 2) / 3, currencyCode), tone: "success" },
    { label: "150%", value: formatCompact(hi, currencyCode), tone: "info" },
  ];
}

const DRAFT_VERSION_LABEL = "Draft version";

const VERSION_ROW_STATUS = {
  [PAY_RANGE_STATUS.DRAFT]: "draft",
  [PAY_RANGE_STATUS.VOIDED]: "voided",
  [PAY_RANGE_STATUS.RETIRED]: "retired",
};

/** Row key of the Status the API sent for a version; State (Lapsed / Current / Future) is separate. */
function versionRowStatus(version) {
  return VERSION_ROW_STATUS[normalizeVersionStatus(version)] || "in-force";
}

function appendStagedDraftRow(rows, api) {
  if (!hasVersionDraft(api) || rows.some((row) => row.status === "draft")) return rows;
  const snap = api.compensation_snapshot || {};
  rows.push({
    key: "draft-staged",
    versionNumber: null,
    versionLabel: DRAFT_VERSION_LABEL,
    status: "draft",
    startDate: api.draft_effective_from || null,
    endDate: null,
    minAmount: toNumber(api.draft_min_amount) ?? toNumber(snap.min_amount),
    maxAmount: toNumber(api.draft_max_amount) ?? toNumber(snap.max_amount),
    usageCount: 0,
  });
  return rows;
}

/**
 * Build per-version history rows for PayRangeVersionHistoryList (ER-11243).
 *
 * Consumes the backend `versions` array (per-version history + usage counts)
 * once the cross-repo backend half of ER-11243 ships. Until then it derives
 * the current + staged-draft rows from the detail payload the list/detail
 * serializer exposes today (usageCount defaults to 0).
 *
 * @param {object} api PayRangeV2 detail payload
 * @returns {{ key: string, versionNumber: number|null, versionLabel: string, versionId?: number|null,
 *   status: string, startDate: string|null, endDate: string|null, minAmount: number|null,
 *   maxAmount: number|null, correctedAt?: string|null, correctedBy?: number|null,
 *   correctedByName?: string, usageCount: number }[]}
 */
export function buildVersionHistory(api = {}) {
  const snap = api.compensation_snapshot || {};
  const rows = [];

  const backendVersions = Array.isArray(api.versions) ? api.versions : null;
  if (backendVersions && backendVersions.length) {
    // A retired / archived range has no open-ended version: its last one ends on the retire date.
    const cardState = resolveCardState(api);
    const closedOn =
      cardState === "retired" || cardState === "archived"
        ? api.retired_on || snap.effective_to || null
        : null;
    backendVersions.forEach((v, index) => {
      const status = versionRowStatus(v);
      // Rule 33: a Draft has no number until it is published, so it never gets its array position.
      const versionNumber = Number(v.version_number) || (status === "draft" ? null : index + 1);
      rows.push({
        key: versionNumber == null ? `draft-${v.id ?? index + 1}` : `v${versionNumber}`,
        versionNumber,
        versionLabel: versionNumber == null ? DRAFT_VERSION_LABEL : `Version ${versionNumber}`,
        versionId: v.id ?? null,
        status,
        startDate: v.effective_from || null,
        endDate:
          v.effective_to || v.superseded_at || (status === "in-force" || status === "retired" ? closedOn : null),
        minAmount: toNumber(v.min_amount),
        maxAmount: toNumber(v.max_amount),
        // ER-12600: a corrected version keeps its row and gains the marker.
        correctedAt: v.corrected_at || null,
        correctedBy: v.corrected_by ?? null,
        correctedByName: resolveCorrectedByName({
          correctedByName: v.corrected_by_name,
          correctedBy: v.corrected_by,
        }),
        usageCount: Math.max(
          0,
          Number(v.used_by_pay_plans ?? v.usage_count ?? v.pay_plan_count ?? 0)
        ),
      });
    });
    return appendStagedDraftRow(rows, api);
  }

  // Fallback derivation — the serializer only exposes the active snapshot +
  // a staged draft, so this is at most current + draft (pre-backend half).
  const state = resolveCardState(api);
  if (state === "first-draft" || state === "archived") return rows;

  const versionNumber = snap.version_number || 1;
  const start =
    snap.effective_from ||
    (api.created_on ? String(api.created_on).slice(0, 10) : null) ||
    new Date().toISOString().slice(0, 10);
  const retired = state === "retired";

  rows.push({
    key: `v${versionNumber}`,
    versionNumber,
    versionLabel: `Version ${versionNumber}`,
    status: "in-force",
    startDate: start,
    endDate: retired ? snap.effective_to || api.retired_on || null : null,
    minAmount: toNumber(snap.min_amount),
    maxAmount: toNumber(snap.max_amount),
    usageCount: 0,
  });

  return appendStagedDraftRow(rows, api);
}

/** The Draft version row of a PUBLISHED range, as listed in `api.versions` (Rule 19). */
function findDraftVersion(api) {
  return Array.isArray(api.versions)
    ? api.versions.find((v) => normalizeVersionStatus(v) === PAY_RANGE_STATUS.DRAFT)
    : undefined;
}

function hasVersionDraft(api) {
  return (
    api.draft_min_amount != null ||
    api.draft_max_amount != null ||
    Boolean(api.draft_effective_from) ||
    Boolean(findDraftVersion(api))
  );
}

/**
 * Map API status + draft fields → PayRangeCard `state`.
 */
export function resolveCardState(api = {}) {
  const status = String(api.status || "").toUpperCase();
  const snap = api.compensation_snapshot || {};
  if (status === "DRAFT") return "first-draft";
  if (status === "ARCHIVED") return "archived";
  if (status === "RETIRED") return "retired";
  if (status === "PUBLISHED" || status === "IN_FORCE") {
    if (hasVersionDraft(api)) return "published-multi";
    if (Number(snap.version_count) > 1) return "published-multi-no-draft";
    return "current";
  }
  return "first-draft";
}

/**
 * Build timeline versions from detail payload.
 *
 * Prefers the full `versions` history list returned by the serializer
 * (ER-11549) so every superseded version renders as a "passed" bar. Falls
 * back to the active snapshot + staged draft fields until the backend ships.
 */
export function buildTimelineVersions(api = {}) {
  const state = resolveCardState(api);
  if (state === "first-draft" || state === "archived") {
    return [];
  }

  const history = Array.isArray(api.versions) ? api.versions : [];
  if (history.length) {
    return buildTimelineVersionsFromHistory(api, history, state);
  }

  return buildTimelineVersionsFromSnapshot(api, state);
}

function isActiveVersion(version) {
  return version.active !== false && !version.superseded_at;
}

function compareVersions(a, b) {
  const aNumber = Number(a.version_number);
  const bNumber = Number(b.version_number);
  if (
    Number.isFinite(aNumber) &&
    Number.isFinite(bNumber) &&
    aNumber !== bNumber
  ) {
    return aNumber - bNumber;
  }
  return String(a.effective_from || "").localeCompare(
    String(b.effective_from || "")
  );
}

function toTimelineVersionNumber(version, index) {
  const parsed = Number(version?.version_number);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : index + 1;
}

function buildCurrentVersion(version, versionNumber) {
  return {
    version: versionNumber,
    state: "current",
    bottom: toNumber(version.min_amount),
    top: toNumber(version.max_amount),
    effectiveFrom: version.effective_from || null,
    effectiveTo: null,
  };
}

function buildPassedVersion(version, versionNumber, endDate, retired = false) {
  return {
    version: versionNumber,
    state: "past",
    bottom: toNumber(version.min_amount),
    top: toNumber(version.max_amount),
    effectiveFrom: version.effective_from || null,
    effectiveTo: endDate,
    retired,
  };
}

function buildDraftBar(api, versionNumber) {
  const snap = api.compensation_snapshot || {};
  const draftRow = findDraftVersion(api);
  const min =
    toNumber(api.draft_min_amount) ?? toNumber(draftRow?.min_amount) ?? toNumber(snap.min_amount);
  const max =
    toNumber(api.draft_max_amount) ?? toNumber(draftRow?.max_amount) ?? toNumber(snap.max_amount);
  const draftStart =
    api.draft_effective_from ||
    draftRow?.effective_from ||
    new Date().toISOString().slice(0, 10);
  return {
    version: versionNumber,
    state: "draft",
    bottom: min,
    top: max,
    effectiveFrom: draftStart,
    effectiveTo: null,
  };
}

function buildTimelineVersionsFromHistory(api, history, state) {
  const snap = api.compensation_snapshot || {};
  const sorted = [...history].sort(compareVersions);
  const isRetired = state === "retired";

  const versions = sorted.map((version, index) => {
    const versionNumber = toTimelineVersionNumber(version, index);
    const isLast = index === sorted.length - 1;

    if (!isRetired && isActiveVersion(version)) {
      return buildCurrentVersion(version, versionNumber);
    }

    const next = sorted[index + 1];
    let endDate = version.effective_to || next?.effective_from || null;
    if (isRetired && isLast) {
      endDate =
        version.effective_to || api.retired_on || snap.effective_to || endDate;
    }
    return buildPassedVersion(
      version,
      versionNumber,
      endDate,
      isRetired && isLast
    );
  });

  if (!isRetired && state === "published-multi") {
    const active = sorted.find(isActiveVersion) || sorted[sorted.length - 1];
    const currentVersionNumber = toTimelineVersionNumber(
      active,
      sorted.length - 1
    );
    versions.push(buildDraftBar(api, currentVersionNumber + 1));
  }

  return versions;
}

function buildTimelineVersionsFromSnapshot(api, state) {
  const snap = api.compensation_snapshot || {};
  const versions = [];

  const versionNumber = snap.version_number || 1;
  const min = toNumber(snap.min_amount);
  const max = toNumber(snap.max_amount);
  const start =
    snap.effective_from ||
    (api.created_on ? String(api.created_on).slice(0, 10) : null) ||
    new Date().toISOString().slice(0, 10);

  if (state === "retired") {
    versions.push({
      version: versionNumber,
      state: "past",
      /** Explicit retired marker flag (ER-11216) — the timeline draws a
       *  thick end-cap + "Retired" label at this version's end date. */
      retired: true,
      bottom: min,
      top: max,
      effectiveFrom: start,
      effectiveTo: snap.effective_to || api.retired_on || null,
    });
    return versions;
  }

  // Current / published (± draft)
  versions.push({
    version: versionNumber,
    state: "current",
    bottom: min,
    top: max,
    effectiveFrom: start,
    effectiveTo: null,
  });

  if (state === "published-multi") {
    versions.push(buildDraftBar(api, versionNumber + 1));
  }

  return versions;
}

/**
 * Maps PayRangeListSerializer detail → PayRangeCard props.
 * @param {object} api
 * @returns {object}
 */
export function mapPayRangeDetailToCard(api = {}) {
  const state = resolveCardState(api);
  const snap = api.compensation_snapshot || {};
  const country = api.operating_country || {};
  const currency = api.operating_currency || {};
  const location = formatPayRangeLocationLabel(api);
  const region =
    api.operating_region ||
    api.location_region?.display_label ||
    api.location_region?.name ||
    "";

  const payBasis = api.pay_basis || "ANNUAL";
  const currencyCode = currency.code || "USD";
  const unit = PAY_BASIS_UNIT[payBasis] || "year";
  const unitSuffix = `${currencyCode}/${unit}`;
  const payUnit = unitSuffix;
  // Timeline bar range prefix: "$" for USD, otherwise the currency code plus a
  // space (e.g. "RON ") so labels render "RON 40K-RON 70K" (ER-11388).
  const currencySymbol = CURRENCY_SYMBOLS[currencyCode] || `${currencyCode} `;

  const min =
    toNumber(snap.min_amount) ??
    toNumber(api.draft_min_amount) ??
    null;
  const max =
    toNumber(snap.max_amount) ??
    toNumber(api.draft_max_amount) ??
    null;

  const versionNumber = snap.version_number || 1;
  let effectiveLine = "";
  let draftPendingLabel = "";

  if (state === "first-draft") {
    effectiveLine = "";
  } else if (state === "retired") {
    effectiveLine = [
      snap.effective_from
        ? `Effective ${formatFairPayDate(snap.effective_from)}`
        : null,
      api.retired_on
        ? `Retired ${formatFairPayDate(api.retired_on)}`
        : null,
      `Version ${versionNumber}`,
    ]
      .filter(Boolean)
      .join(" · ");
  } else if (state === "archived") {
    effectiveLine = [
      snap.effective_from
        ? `Effective ${formatFairPayDate(snap.effective_from)}`
        : null,
      api.retired_on
        ? `Retired ${formatFairPayDate(api.retired_on)}`
        : null,
      api.archived_on
        ? `Archived ${formatFairPayDate(api.archived_on)}`
        : null,
    ]
      .filter(Boolean)
      .join(" · ");
  } else {
    effectiveLine = [
      snap.effective_from
        ? `Effective since ${formatFairPayDate(snap.effective_from)}`
        : null,
      `Version ${versionNumber}`,
    ]
      .filter(Boolean)
      .join(" · ");
  }

  if (state === "published-multi" && api.draft_effective_from) {
    draftPendingLabel = `Draft pending · eff. ${formatFairPayDate(
      api.draft_effective_from
    )}`;
  } else if (state === "published-multi") {
    draftPendingLabel = "Draft pending";
  }

  const versions = buildTimelineVersions(api);
  const handoffDate =
    state === "published-multi" ? api.draft_effective_from || null : null;

  return {
    state,
    statusBadge: rangeStatusBadge(api),
    archivedTag: archivedTag(api),
    name:
      api.pay_range_name ||
      api.name ||
      api.job_family_level?.label ||
      "Pay Range",
    code: api.code || "",
    flag: resolveFlag(country),
    location,
    workMode:
      api.work_mode && String(api.work_mode).toUpperCase() !== "UNSPECIFIED"
        ? WORK_MODE_LABEL[api.work_mode] || api.work_mode || ""
        : "",
    payUnit,
    unitSuffix,
    currencySymbol,
    readiness: buildReadiness(min, max, currencyCode),
    effectiveLine,
    draftPendingLabel,
    versions,
    handoffDate,
    handoffLabel: "Effective Date",
    // Helpers for wizards / dialogs (not card props)
    _meta: {
      id: api.id,
      status: String(api.status || "").toUpperCase(),
      payBasis,
      expressedAs: PAY_BASIS_TO_EXPRESSED_AS[payBasis] || "per_year",
      currencyCode,
      countryFlag: resolveFlag(country),
      location,
      min,
      max,
      versionNumber,
      effectiveFrom: snap.effective_from || null,
      draftMin: toNumber(api.draft_min_amount) ?? toNumber(findDraftVersion(api)?.min_amount),
      draftMax: toNumber(api.draft_max_amount) ?? toNumber(findDraftVersion(api)?.max_amount),
      draftEffectiveFrom:
        api.draft_effective_from || findDraftVersion(api)?.effective_from || null,
      draftSources: api.draft_sources || "",
      hasVersionDraft: hasVersionDraft(api),
      history: buildVersionHistory(api),
      operatingCountryId: country.id ?? null,
      operatingCurrencyId: currency.id ?? null,
      workModeCode: api.work_mode || "UNSPECIFIED",
      operatingRegion: region,
    },
  };
}
